import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, Mail, Tag } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { getEmailApiBaseUrl } from '../../utils/emailApi';
import { getEmailTagDisplayName } from '../../utils/emailTagLabel';
import LoadingSpinner from '../common/LoadingSpinner';
import ClientContactLogTab from './ClientContactLogTab';

const TAG_COLORS = {
  green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  gray: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-white/90',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  violet: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
  amber: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
};

function tagChipClass(color) {
  return TAG_COLORS[color] || TAG_COLORS.blue;
}

export default function MemberCommunicationsTab({ memberId, memberEmail }) {
  const { getAuthenticatedApi } = useAuth();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const emailApi = getEmailApiBaseUrl();

  const [loading, setLoading] = useState(true);
  const [savingOptIn, setSavingOptIn] = useState(false);
  const [tagBusyId, setTagBusyId] = useState(null);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [assignedTags, setAssignedTags] = useState([]);
  const [allCustomTags, setAllCustomTags] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const api = getAuthenticatedApi();
      const [memberRes, listRes] = await Promise.all([
        api.get(`gym_email_tags.php?action=member_tags&user_id=${memberId}`, { baseURL: emailApi }),
        api.get('gym_email_tags.php?action=list', { baseURL: emailApi }),
      ]);
      setAssignedTags(memberRes.data?.tags || []);
      setMarketingOptIn(!!memberRes.data?.marketing_opt_in);
      const custom = (listRes.data?.tags || []).filter((tag) => !tag.is_system);
      setAllCustomTags(custom);
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.membersManager.communications.loadError'));
    } finally {
      setLoading(false);
    }
  }, [getAuthenticatedApi, emailApi, memberId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOptInChange = async (checked) => {
    setSavingOptIn(true);
    try {
      const api = getAuthenticatedApi();
      await api.post(
        'gym_email_tags.php?action=set_marketing_opt_in',
        { user_id: memberId, marketing_opt_in: checked },
        { baseURL: emailApi }
      );
      setMarketingOptIn(checked);
      toast.success(
        checked
          ? t('gym.membersManager.communications.optInEnabled')
          : t('gym.membersManager.communications.optInDisabled')
      );
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.membersManager.communications.optInSaveError'));
    } finally {
      setSavingOptIn(false);
    }
  };

  const assignedCustomIds = new Set(
    assignedTags.filter((tag) => !tag.is_system).map((tag) => tag.id)
  );

  const toggleCustomTag = async (tag) => {
    const isAssigned = assignedCustomIds.has(tag.id);
    setTagBusyId(tag.id);
    try {
      const api = getAuthenticatedApi();
      if (isAssigned) {
        await api.post(
          'gym_email_tags.php?action=unassign',
          { user_id: memberId, tag_id: tag.id },
          { baseURL: emailApi }
        );
      } else {
        await api.post(
          'gym_email_tags.php?action=assign',
          { user_id: memberId, tag_id: tag.id },
          { baseURL: emailApi }
        );
      }
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || t('gym.membersManager.communications.tagSaveError'));
    } finally {
      setTagBusyId(null);
    }
  };

  const hasEmail = !!(memberEmail && String(memberEmail).trim());
  const eligibleCampaigns = marketingOptIn && hasEmail;

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  const systemTags = assignedTags.filter((tag) => tag.is_system);

  return (
    <div className="space-y-6">
      <div>
        <h3 className={`font-semibold text-gray-900 dark:text-white ${isMobile ? 'text-base' : 'text-lg'}`}>
          {t('gym.membersManager.communications.title')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-white/65">
          {t('gym.membersManager.communications.subtitle')}
        </p>
      </div>

      {/* Consenso marketing */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-gray-900/80">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={marketingOptIn}
            disabled={savingOptIn}
            onChange={(e) => handleOptInChange(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-white/30 dark:bg-gray-800"
          />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-gray-900 dark:text-white">
              {t('gym.membersManager.communications.optInLabel')}
            </span>
            <span className="mt-1 block text-xs text-gray-500 dark:text-white/60">
              {t('gym.membersManager.communications.optInHint')}
            </span>
          </span>
          {savingOptIn && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />}
        </label>

        <div className="mt-4 flex flex-wrap gap-2 text-sm">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium ${
              eligibleCampaigns
                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-white/80'
            }`}
          >
            <Mail className="h-3.5 w-3.5" aria-hidden />
            {eligibleCampaigns
              ? t('gym.membersManager.communications.eligibleYes')
              : t('gym.membersManager.communications.eligibleNo')}
          </span>
          {!hasEmail && (
            <span className="text-xs text-amber-700 dark:text-amber-200">
              {t('gym.membersManager.communications.noEmail')}
            </span>
          )}
        </div>
      </div>

      {/* Tag sistema */}
      {systemTags.length > 0 && (
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-white/55">
            {t('gym.membersManager.communications.systemTags')}
          </p>
          <div className="flex flex-wrap gap-2">
            {systemTags.map((tag) => (
              <span
                key={tag.id}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${tagChipClass(tag.color)}`}
              >
                <Tag className="h-3 w-3 opacity-70" aria-hidden />
                {getEmailTagDisplayName(tag, t)}
              </span>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-white/50">
            {t('gym.membersManager.communications.systemTagsHint')}
          </p>
        </div>
      )}

      {/* Tag personalizzati */}
      <div>
        <p className="mb-2 text-sm font-medium text-gray-900 dark:text-white">
          {t('gym.membersManager.communications.customTags')}
        </p>
        {allCustomTags.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-white/65">
            {t('gym.membersManager.communications.noCustomTags')}{' '}
            <Link to="/gym/email" className="font-medium text-indigo-600 hover:underline dark:text-[#cba5fa]">
              {t('gym.membersManager.communications.manageTagsLink')}
            </Link>
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {allCustomTags.map((tag) => {
              const active = assignedCustomIds.has(tag.id);
              const busy = tagBusyId === tag.id;
              return (
                <button
                  key={tag.id}
                  type="button"
                  disabled={busy}
                  onClick={() => toggleCustomTag(tag)}
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    active
                      ? 'border-indigo-400 bg-indigo-100 text-indigo-900 dark:border-[#cba5fa]/50 dark:bg-indigo-900/50 dark:text-indigo-100'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 dark:border-white/20 dark:bg-gray-800/50 dark:text-white/85 dark:hover:bg-gray-700'
                  }`}
                >
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Tag className="h-3 w-3" aria-hidden />}
                  {tag.name}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Storico contatti manuali */}
      <details
        open={historyOpen}
        onToggle={(e) => setHistoryOpen(e.target.open)}
        className="rounded-xl border border-gray-200 dark:border-white/10"
      >
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 rounded-xl bg-gray-50 px-4 py-3 text-sm font-medium text-gray-800 dark:bg-gray-800/60 dark:text-white [&::-webkit-details-marker]:hidden">
          {t('gym.membersManager.communications.manualHistoryTitle')}
          <ChevronDown
            className={`h-4 w-4 shrink-0 transition-transform ${historyOpen ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </summary>
        <div className="border-t border-gray-200 p-4 dark:border-white/10">
          <ClientContactLogTab memberId={memberId} embedded />
        </div>
      </details>
    </div>
  );
}
